/**
 * ProtectedRoute Component
 * Protects routes that require authentication
 *
 * Auth gate matrix (when requiredRole !== "admin"):
 *   - local / manus_dev      → public (no auth gate)
 *   - firebase_prod          → public (full public access)
 *   - firebase_uat           → public only on limited routes (/topup, /games)
 *
 * Admin routes (requiredRole === "admin") always require authentication
 * regardless of environment.
 */

import { useEffect } from "react";
import { useLocation } from "wouter";
import { useUnifiedAuth } from "@/contexts/UnifiedAuthContext";
import { getEnvironment, getAuthType } from "@/lib/envDetection";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "user" | "admin";
}

const UAT_PUBLIC_ROUTES = ["/topup", "/games"];

function isUatPublicPath(pathname: string): boolean {
  if (pathname.startsWith("/topup/")) return true;
  return UAT_PUBLIC_ROUTES.includes(pathname);
}

export default function ProtectedRoute({
  children,
  requiredRole = "user",
}: ProtectedRouteProps) {
  const [, setLocation] = useLocation();
  const { user, loading, isAuthenticated } = useUnifiedAuth();

  const env = getEnvironment();
  const authType = getAuthType();
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";

  const isLoginPage = pathname === "/login";
  const isMausEnv = env === "local" || env === "manus_dev" || authType === "manus";
  const isFirebaseProd = env === "firebase_prod";
  const isFirebaseUat = env === "firebase_uat";

  // Decide whether to skip the auth gate. Admin routes never skip.
  const allowPublicAccess =
    requiredRole !== "admin" &&
    (isLoginPage ||
      isMausEnv ||
      isFirebaseProd ||
      (isFirebaseUat && isUatPublicPath(pathname)));

  // Redirect to login if not authenticated (skip when public access is allowed)
  useEffect(() => {
    if (allowPublicAccess) return;
    if (!loading && !isAuthenticated) {
      setLocation("/login");
    }
  }, [isAuthenticated, loading, setLocation, allowPublicAccess]);

  // Redirect if authenticated user doesn't have required role
  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      if (requiredRole === "admin" && user.role !== "admin") {
        setLocation("/");
      }
    }
  }, [user, loading, isAuthenticated, requiredRole, setLocation]);

  // Public access — render content without auth gate
  if (allowPublicAccess) {
    return <>{children}</>;
  }

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  // Show access denied if user doesn't have required role
  if (isAuthenticated && user && requiredRole === "admin" && user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-white/60 mb-8">You don't have permission to access this page.</p>
          <button
            onClick={() => setLocation("/")}
            className="bg-white text-black px-6 py-2 font-bold hover:bg-white/90 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Render protected content if authenticated
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Should not reach here due to redirect above, but just in case
  return null;
}
